import { DownloadIcon } from '@components/Icons/DownloadIcon';
import { PlayIcon } from '@components/Icons/PlayIcon';
import { SaveIcon } from '@components/Icons/SaveIcon';
import { ShareIcon } from '@components/Icons/ShareIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import {
  useDeleteMyCreation,
  useUpdateMyCreation,
} from '@hooks/apiHooks/myCreationsHooks';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useMyCreationsInfiniteScroll from '@hooks/useMyCreationsInfiniteScroll';
import {
  getCurrentUserFullName,
  trackCreationDownloaded,
  trackCreationShare,
} from '@utils/analyticsHelpers';
import { getOrganizationId } from '@utils/getOrganization';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import { placeHolderData } from '../../config';
import { SHARE_DESTINATIONS } from '../../constants/analytics';
import { MyCreation } from '../../services/myCreationsApi';
import { OidcUser } from '../../types/oidcUser';
import { ConfirmDeleteCreationModal } from './components/ConfirmDeleteCreationModal';
import { MyCreationsShareTooltip } from './components/MyCreationsShareTooltip/MyCreationsShareTooltip';

export const MyCreationsPage = memo(() => {
  const { t } = useTranslation();
  const { setActivePlaylist, addRecentTrack } = useMusicState();
  const { setActivePopup } = useCommonState();
  const [deletingItems, setDeletingItems] = useState<Set<string>>(new Set());
  const [savingTitleItems, setSavingTitleItems] = useState<Set<string>>(new Set());
  const [editingCreationId, setEditingCreationId] = useState<string | null>(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [shareTooltipOpen, setShareTooltipOpen] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const shareButtonRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const orgId = getOrganizationId(user as OidcUser);

  // Infinite scroll hooks for each asset type
  // Enable all hooks to always have accurate counts, similar to Favorites page
  const tracksCreations = useMyCreationsInfiniteScroll({
    assetType: 'Track',
    enabled: true, // Always enabled to maintain accurate counts
    scrollContainerRef,
  });

  const { mutate: deleteCreation } = useDeleteMyCreation();
  const { mutate: updateCreation } = useUpdateMyCreation();

  // Get current data based on active tab
  const currentData = useMemo(() => {
    return tracksCreations;
  }, [tracksCreations]);

  // Get counts from the API (always use hook totals for accurate counts)
  const tracksCount = tracksCreations.total;

  const hasNoData = currentData.items.length === 0 && !currentData.loading;

  const handleDeleteClick = useCallback((id: string, name: string) => {
    setConfirmDelete({ id, name });
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setConfirmDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!confirmDelete) return;

    const { id, name } = confirmDelete;
    setDeletingItems((prev) => new Set(prev).add(id));

    deleteCreation(id, {
      onSuccess: () => {
        toast.success(t('creations.deletedSuccessfully', { name }));
        setDeletingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setConfirmDelete(null);
        tracksCreations.refresh();
        // A deleted creation may still be referenced by user playlists.
        // Tell any mounted playlists view to refresh so its track counts /
        // thumbnails reflect the removal without a manual reload.
        window.dispatchEvent(new CustomEvent('playlistUpdated'));
      },
      onError: (error) => {
        console.error('Failed to delete item:', error);
        toast.error(t('creations.deleteFailed'));
        setDeletingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setConfirmDelete(null);
      },
    });
  }, [confirmDelete, deleteCreation, tracksCreations]);

  const startTitleEdit = useCallback((creation: MyCreation) => {
    if (deletingItems.has(creation.id)) return;
    setEditingCreationId(creation.id);
    setEditingTitleValue(creation.data.title || '');
  }, [deletingItems]);

  const cancelTitleEdit = useCallback(() => {
    setEditingCreationId(null);
    setEditingTitleValue('');
  }, []);

  const saveTitleEdit = useCallback(
    (creation: MyCreation) => {
      const nextTitle = editingTitleValue.trim();
      if (!nextTitle) {
        toast.error(t('creations.titleEmpty'));
        return;
      }

      if (nextTitle === (creation.data.title || '').trim()) {
        setEditingCreationId(null);
        return;
      }

      setSavingTitleItems((prev) => new Set(prev).add(creation.id));
      updateCreation(
        {
          id: creation.id,
          data: {
            data: {
              title: nextTitle,
            },
          },
        },
        {
          onSuccess: () => {
            toast.success(t('creations.titleUpdated'));
            setEditingCreationId(null);
            setSavingTitleItems((prev) => {
              const next = new Set(prev);
              next.delete(creation.id);
              return next;
            });
            tracksCreations.refresh();
          },
          onError: (error) => {
            console.error('Failed to update title:', error);
            toast.error(t('creations.titleUpdateFailed'));
            setSavingTitleItems((prev) => {
              const next = new Set(prev);
              next.delete(creation.id);
              return next;
            });
          },
        },
      );
    },
    [editingTitleValue, tracksCreations, updateCreation],
  );

  const handleSaveTitleClick = useCallback(
    (creation: MyCreation) => {
      saveTitleEdit(creation);
    },
    [saveTitleEdit],
  );

  const handleCancelTitleClick = useCallback(() => {
    cancelTitleEdit();
  }, [cancelTitleEdit]);

  const playTrack = useCallback(
    async (creation: MyCreation) => {
      try {
        // Create a single-track album for this creation
        const trackData = creation.data;

        // Hardcoded videoMobilePreviewUrl for station creation
        const HARDCODED_CREATION_ID = '1d88879c-a8f6-484a-bd7a-5b2392356609';
        const hardcodedVideoMobilePreviewUrl =
          creation.id === HARDCODED_CREATION_ID
            ? 'https://napstermedia.azureedge.net/aimusic/contributors/0618a946-4961-47b1-a977-8be9ff5eb6cb/0618a946-4961-47b1-a977-8be9ff5eb6cb_mobile_preview.jpg'
            : undefined;

        const singleTrackAlbum = {
          id: creation.id,
          albumName: trackData.title || 'My Creation',
          albumArtUrl: trackData.albumThumbnail || trackData.previewImage || '',
          contributors: [
            {
              id: creation.contributor.id,
              givenName: creation.contributor.fullName,
              thumbnailUrl: creation.contributor.previewImage,
              videoDesktopUrl: creation.contributor.previewVideo || '',
              videoMobileUrl: creation.contributor.previewMobileVideo || '',
              slug: creation.contributor.slug,
              videoDesktopPreviewUrl:
                creation.contributor.previewVideoImage || '',
              videoMobilePreviewUrl:
                creation.contributor.previewMobileVideoImage || '',
            },
          ],
          tracks: [
            {
              id: creation.id,
              name: trackData.title,
              url: trackData.url,
              duration: trackData.duration || 0,
              lyrics: trackData.lyrics,
              lyricsTimestamped: trackData.lyricsTimestamped,
            },
          ],
          category: 'my-creations',
          likes: 0,
          isLiked: false,
          upc: creation.requestId,
          pk: '',
          description: creation.query,
          genre: trackData.genres || [],
          type: 'my-creations',
          orgId: orgId,
          creator: {
            id: creation.creator.id,
            fullName: creation.creator.fullName,
            email: creation.creator.email,
          },
          ...(hardcodedVideoMobilePreviewUrl && {
            videoMobilePreviewUrl: hardcodedVideoMobilePreviewUrl,
          }),
        };

        const playlist = {
          currentIndex: 0,
          album: singleTrackAlbum,
        };

        // Add to recent tracks
        const recentTrack = {
          track: {
            name: trackData.title,
            url: trackData.url,
            trackIndex: 1,
          },
          contributor: {
            id: creation.contributor.id,
            givenName: creation.contributor.fullName,
            thumbnailUrl: creation.contributor.previewImage,
            videoDesktopUrl: creation.contributor.previewVideo || '',
            videoMobileUrl: creation.contributor.previewMobileVideo || '',
            slug: creation.contributor.slug,
            videoDesktopPreviewUrl:
              creation.contributor.previewVideoImage || '',
            videoMobilePreviewUrl:
              creation.contributor.previewMobileVideoImage || '',
          },
          albumArtUrl: trackData.albumThumbnail || trackData.previewImage || '',
          timestamp: Date.now(),
        };

        addRecentTrack(recentTrack);
        setActivePlaylist(playlist);
        setActivePopup('musicplayer');
      } catch (error) {
        console.error('Error playing track:', error);
        toast.error(t('creations.playFailed'));
      }
    },
    [setActivePlaylist, addRecentTrack, setActivePopup],
  );

  const generateShareUrl = useCallback(
    (creation: MyCreation, assetType: string) => {
      const baseUrl = window.location.origin;
      if (!orgId) return baseUrl;

      if (assetType === 'Track') {
        return `${baseUrl}/creations/track/${orgId}/${creation.id}`;
      } else {
        return `${baseUrl}/creations/image/${orgId}/${creation.id}`;
      }
    },
    [orgId],
  );

  const handleShareClick = useCallback(
    async (creation: MyCreation, assetType: string) => {
      const shareUrl = generateShareUrl(creation, assetType);
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );

      if (navigator.share && isMobile) {
        // Use native share API on mobile
        const shareText = t('creations.shareText', { title: creation.data.title });
        try {
          await navigator.share({
            url: shareUrl,
            title: creation.data.title,
            text: shareText,
          });
          // Analytics: creation_share via Native Share
          const creatorName = getCurrentUserFullName(user as OidcUser | null);
          trackCreationShare({
            contentId: creation.id,
            contentType: 'Music',
            contentName: creation.data.title,
            creatorName: creatorName,
            shareDestination: SHARE_DESTINATIONS.NATIVE_SHARE,
          });
        } catch (err) {
          // User cancelled or share failed, fall back to tooltip
          console.log('Native share cancelled, showing tooltip', err);
          setShareTooltipOpen(creation.id);
        }
      } else {
        // Use tooltip on desktop
        setShareTooltipOpen(creation.id);
      }
    },
    [generateShareUrl],
  );

  const handleDownload = useCallback((creation: MyCreation) => {
    try {
      const link = document.createElement('a');
      link.href = creation.data.url;
      link.download = `${creation.data.title}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(t('creations.downloading', { title: creation.data.title }));

      // Analytics: creation_downloaded
      trackCreationDownloaded({
        creationId: creation.id,
        creationType: 'Music',
        creationName: creation.data.title,
        creationCompanion: creation.contributor.fullName || 'Unknown',
      });
    } catch (error) {
      console.error('Error downloading track:', error);
      toast.error(t('creations.downloadFailed'));
    }
  }, []);

  return (
    <div className="flex flex-col h-full pt-0 sm:pt-[10px]">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 relative z-40">
        <div className="flex flex-col">
          <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
            {t('creations.title')}
          </h1>
          <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
            {t('creations.subtitle')}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto na-scroll-container">
        <div className="px-4 sm:px-6 pb-[125px]">
          {/* Loading State - Skeleton Grid */}
          {currentData.loading && currentData.items.length === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="flex flex-col animate-pulse">
                  <div className="w-full aspect-square rounded-[8px] bg-gray-700 mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          )}

          {/* Content when not loading */}
          {(!currentData.loading || currentData.items.length > 0) && (
            <>
              {/* Empty State */}
              {hasNoData && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="mb-4 opacity-50">
                    <SaveIcon width={48} height={48} fill="white" />
                  </div>
                  <h3 className="text-white text-lg font-medium mb-2">
                    {t('creations.noCreationsYet')}
                  </h3>
                  <p className="text-white/60 text-sm text-center max-w-sm">
                    {t('creations.startGenerating')}
                  </p>
                </div>
              )}

              {!hasNoData && (
                <div className="space-y-8">
                  {/* Tracks Section */}
                  {tracksCreations.items.length > 0 && (
                    <div>
                      <h3 className="text-white text-xl text-left font-bold mb-4">
                        {t('creations.tracksCount', { count: tracksCount })}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4 overflow-visible">
                        {tracksCreations.items.map(
                          (creation: MyCreation, index: number) => (
                            <div
                              key={`creation-track-${creation.id || index}`}
                              className="flex flex-col cursor-pointer hover:scale-105 transition-transform duration-300"
                              onMouseLeave={() => {
                                if (shareTooltipOpen === creation.id) {
                                  setShareTooltipOpen(null);
                                }
                              }}>
                              {/* Album Image with Hover Effects */}
                              <div className="relative w-full aspect-square rounded-[8px] overflow-visible mb-3 group">
                                {/* Album Image */}
                                <img
                                  src={
                                    creation.data.albumThumbnail ||
                                    creation.data.previewImage ||
                                    placeHolderData.albumCover
                                  }
                                  alt={creation.data.title}
                                  className="w-full h-full object-cover"
                                  onClick={() => playTrack(creation)}
                                />

                                {/* Hover overlay with play, download, share and delete buttons */}
                                <div className="absolute inset-0 bg-black/40 opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                                  {/* Play Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      playTrack(creation);
                                    }}
                                    className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200">
                                    <PlayIcon width={20} height={20} />
                                  </button>

                                  {/* Download Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(creation);
                                    }}
                                    className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200">
                                    <DownloadIcon
                                      color="white"
                                      width={20}
                                      height={20}
                                    />
                                  </button>

                                  {/* Share Button */}
                                  <div
                                    className="relative z-[100]"
                                    ref={(el) => {
                                      if (el)
                                        shareButtonRefs.current.set(
                                          creation.id,
                                          el,
                                        );
                                    }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleShareClick(creation, 'Track');
                                      }}
                                      className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200">
                                      <ShareIcon
                                        color="white"
                                        width={20}
                                        height={20}
                                      />
                                    </button>
                                    {shareTooltipOpen === creation.id && (
                                      <MyCreationsShareTooltip
                                        isOpen={true}
                                        onClose={() =>
                                          setShareTooltipOpen(null)
                                        }
                                        trackName={creation.data.title}
                                        shareUrl={generateShareUrl(
                                          creation,
                                          'Track',
                                        )}
                                        title={t('share.title')}
                                        triggerRef={{
                                          current:
                                            shareButtonRefs.current.get(
                                              creation.id,
                                            ) || null,
                                        }}
                                      />
                                    )}
                                  </div>

                                  {/* Delete Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(
                                        creation.id,
                                        creation.data.title,
                                      );
                                    }}
                                    disabled={deletingItems.has(creation.id)}
                                    className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200 disabled:opacity-50">
                                    {deletingItems.has(creation.id) ? (
                                      <NapsterSpinner size="small" />
                                    ) : (
                                      <i className="fas fa-trash text-white text-lg" />
                                    )}
                                  </button>
                                </div>
                              </div>

                              {/* Track Info */}
                              <div className="flex flex-col">
                                {editingCreationId === creation.id ? (
                                  <div className="flex items-center gap-2 mb-1">
                                    <input
                                      value={editingTitleValue}
                                      onChange={(event) =>
                                        setEditingTitleValue(event.target.value)
                                      }
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault();
                                          handleSaveTitleClick(creation);
                                        }
                                        if (event.key === 'Escape') {
                                          event.preventDefault();
                                          handleCancelTitleClick();
                                        }
                                      }}
                                      className="w-full bg-transparent border-b border-white/30 text-white text-sm font-medium leading-[140%] outline-none"
                                      autoFocus
                                    />
                                    {savingTitleItems.has(creation.id) ? (
                                      <NapsterSpinner size="small" />
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          onClick={handleCancelTitleClick}
                                          className="text-white/70 hover:text-white text-xs font-semibold transition-colors">
                                          {t('common.cancel')}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleSaveTitleClick(creation)}
                                          className="text-[#fb923c] text-xs font-semibold">
                                          {t('common.save')}
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="text-white text-sm font-medium leading-[140%] line-clamp-2">
                                      {creation.data.title}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => startTitleEdit(creation)}
                                      aria-label={t('common.editTitle')}
                                      className="text-white/70 hover:text-white transition-colors flex items-center justify-center">
                                      <i className="fas fa-pencil-alt text-[12px]" />
                                    </button>
                                  </div>
                                )}
                                <div className="text-white/60 text-xs leading-[140%] line-clamp-1">
                                  {creation.contributor.fullName}
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>

                      {/* Load More Button for Tracks */}
                      {tracksCreations.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() => tracksCreations.loadMore()}
                            disabled={tracksCreations.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {tracksCreations.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              t('creations.loadMoreTracks')
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmDelete && (
        <ConfirmDeleteCreationModal
          isOpen={!!confirmDelete}
          creationName={confirmDelete.name}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isLoading={deletingItems.has(confirmDelete.id)}
        />
      )}
    </div>
  );
});

MyCreationsPage.displayName = 'MyCreationsPage';
